import { LitElement, html, type CSSResultGroup, type PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { panelStyles, splitterStyles } from './styles.js';
import { emit } from '../utils/dom.utils.js';

// ── Types ─────────────────────────────────────────────────────────────────────

export type SplitterLayout       = 'horizontal' | 'vertical';
export type SplitterStateStorage = 'local' | 'session';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-splitter-panel
// ══════════════════════════════════════════════════════════════════════════════

export class UwcSplitterPanel extends LitElement {
  static override styles: CSSResultGroup = panelStyles;

  /** Initial size as a percentage (0–100). Omit for equal distribution. */
  @property({ type: Number }) size: number | null = null;

  /** Minimum panel size as a percentage. Default: 0. */
  @property({ type: Number, attribute: 'min-size' }) minSize = 0;

  protected override render() {
    return html`<slot></slot>`;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// uwc-splitter
// ══════════════════════════════════════════════════════════════════════════════

export class UwcSplitter extends LitElement {
  static override styles: CSSResultGroup = splitterStyles;

  // ── Public properties ─────────────────────────────────────────────────────

  /** Orientation of the split. Default: 'horizontal'. */
  @property({ reflect: true }) layout: SplitterLayout = 'horizontal';

  /** Width (horizontal) or height (vertical) of the gutter in px. Default: 6. */
  @property({ type: Number, attribute: 'gutter-size' }) gutterSize = 6;

  /** localStorage / sessionStorage key for persisting panel sizes. */
  @property({ attribute: 'state-key' }) stateKey = '';

  /** Storage back-end to use when `state-key` is set. Default: 'local'. */
  @property({ attribute: 'state-storage' }) stateStorage: SplitterStateStorage = 'local';

  /** Percentage step applied per arrow-key press. Default: 5. */
  @property({ type: Number }) step = 5;

  // ── Private state ─────────────────────────────────────────────────────────

  private _panels:        UwcSplitterPanel[] = [];
  private _sizes:         number[]           = [];
  private _gutters:       HTMLElement[]      = [];
  private _handles:       HTMLElement[]      = [];
  private _draggingIndex  = -1;
  private _startPos       = 0;
  private _startSizeA     = 0;
  private _startSizeB     = 0;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._cleanup();
  }

  override firstUpdated() {
    this._setup();
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);
    if (changed.has('layout') || changed.has('gutterSize')) {
      this._cleanup();
      this._setup();
    }
  }

  // ── Setup / teardown ──────────────────────────────────────────────────────

  private _setup() {
    this._panels = Array.from(this.children).filter(
      (el): el is UwcSplitterPanel => el instanceof UwcSplitterPanel,
    );
    if (this._panels.length < 2) return;

    this._sizes = this._loadState() ?? this._defaultSizes();
    this._applyPanelStyles();
    this._injectGutters();
  }

  private _cleanup() {
    this._gutters.forEach(g => g.remove());
    this._gutters = [];
    this._handles = [];
  }

  private _defaultSizes(): number[] {
    let defined = 0;
    const raw = this._panels.map(p => {
      if (p.size != null) { defined += p.size; return p.size; }
      return -1;
    });
    const unset    = raw.filter(s => s < 0).length;
    const perUnset = unset > 0 ? (100 - defined) / unset : 0;
    return raw.map(s => (s < 0 ? perUnset : s));
  }

  /**
   * Applies flex-basis to each panel using the split.js formula:
   *   calc(X% - X/100 * totalGutterPx)
   * This ensures panels + gutters always sum to exactly 100% of the container.
   */
  private _applyPanelStyles() {
    const isH          = this.layout !== 'vertical';
    const totalGutterPx = (this._panels.length - 1) * this.gutterSize;

    this._panels.forEach((p, i) => {
      const pct      = this._sizes[i];
      const gutterAdj = (pct / 100) * totalGutterPx;
      const basis    = `calc(${pct}% - ${gutterAdj}px)`;

      p.style.flexBasis = basis;
      if (isH) {
        p.style.width     = basis;
        p.style.height    = '100%';
        p.style.minWidth  = p.minSize > 0 ? `calc(${p.minSize}% - ${(p.minSize / 100) * totalGutterPx}px)` : '';
        p.style.minHeight = '';
      } else {
        p.style.height    = basis;
        p.style.width     = '100%';
        p.style.minHeight = p.minSize > 0 ? `calc(${p.minSize}% - ${(p.minSize / 100) * totalGutterPx}px)` : '';
        p.style.minWidth  = '';
      }
    });
  }

  private _injectGutters() {
    const isH = this.layout !== 'vertical';

    for (let i = 0; i < this._panels.length - 1; i++) {
      const gutter = document.createElement('div');
      gutter.className = 'uwc-splitter__gutter';
      gutter.setAttribute('role',             'separator');
      gutter.setAttribute('aria-orientation', isH ? 'vertical' : 'horizontal');
      gutter.setAttribute('aria-valuenow',    String(Math.round(this._sizes[i])));
      gutter.setAttribute('aria-valuemin',    String(this._panels[i].minSize));
      gutter.setAttribute('aria-valuemax',    String(100 - this._panels[i + 1].minSize));
      gutter.setAttribute('aria-label',       `Resize panel ${i + 1}`);
      gutter.setAttribute('tabindex',         '0');

      // Inline styles for the dynamic (gutterSize-dependent) values
      gutter.style.flexBasis        = `${this.gutterSize}px`;
      gutter.style.backgroundColor  = 'var(--uwc-splitter-gutter-bg, transparent)';
      gutter.style.transition       = 'background-color 180ms ease';

      if (isH) {
        gutter.style.width  = `${this.gutterSize}px`;
        gutter.style.height = '100%';
        gutter.style.cursor = 'col-resize';
      } else {
        gutter.style.height = `${this.gutterSize}px`;
        gutter.style.width  = '100%';
        gutter.style.cursor = 'row-resize';
      }

      // ── Handle bar (visual indicator) ─────────────────────────────────
      const handle = document.createElement('div');
      handle.className = 'uwc-splitter__handle';
      handle.style.cssText = `
        pointer-events: none;
        border-radius:  9999px;
        background:     var(--uwc-splitter-handle-color, rgba(0,0,0,0.18));
        transition:     background 200ms ease, transform 200ms ease;
        ${isH
          ? 'width: 3px; height: 36px;'
          : 'width: 36px; height: 3px;'}
      `;
      gutter.appendChild(handle);

      // ── Hover highlight (split.js style) ──────────────────────────────
      gutter.addEventListener('mouseenter', () => {
        if (this._draggingIndex >= 0) return;
        gutter.style.backgroundColor = 'var(--uwc-splitter-gutter-hover-bg, rgba(99,102,241,0.07))';
        handle.style.background      = 'var(--uwc-splitter-handle-hover, #6366f1)';
        handle.style.transform       = isH ? 'scaleX(1.75)' : 'scaleY(1.75)';
      });
      gutter.addEventListener('mouseleave', () => {
        if (this._draggingIndex >= 0) return;
        gutter.style.backgroundColor = 'var(--uwc-splitter-gutter-bg, transparent)';
        handle.style.background      = 'var(--uwc-splitter-handle-color, rgba(0,0,0,0.18))';
        handle.style.transform       = '';
      });

      // ── Drag (mouse) ──────────────────────────────────────────────────
      gutter.addEventListener('mousedown', (e: MouseEvent) => {
        e.preventDefault();
        this._startMouseDrag(e, i);
      });

      // ── Drag (touch) ──────────────────────────────────────────────────
      gutter.addEventListener('touchstart', (e: TouchEvent) => {
        e.preventDefault();
        this._startTouchDrag(e, i);
      }, { passive: false });

      // ── Keyboard ──────────────────────────────────────────────────────
      gutter.addEventListener('keydown', (e: KeyboardEvent) => this._onGutterKeydown(e, i));

      this._panels[i].insertAdjacentElement('afterend', gutter);
      this._gutters.push(gutter);
      this._handles.push(handle);
    }
  }

  // ── Drag — mouse ──────────────────────────────────────────────────────────

  private _startMouseDrag(e: MouseEvent, index: number) {
    const isH = this.layout !== 'vertical';
    this._beginDrag(index, isH ? e.clientX : e.clientY);

    document.body.style.userSelect = 'none';
    document.body.style.cursor     = isH ? 'col-resize' : 'row-resize';

    const onMove = (ev: MouseEvent) => {
      this._moveDrag(index, isH ? ev.clientX : ev.clientY);
    };
    const onUp = () => {
      this._endDrag(index);
      document.body.style.userSelect = '';
      document.body.style.cursor     = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  // ── Drag — touch ──────────────────────────────────────────────────────────

  private _startTouchDrag(e: TouchEvent, index: number) {
    const isH  = this.layout !== 'vertical';
    const touch = e.touches[0];
    this._beginDrag(index, isH ? touch.clientX : touch.clientY);

    const onMove = (ev: TouchEvent) => {
      const t = ev.touches[0];
      this._moveDrag(index, isH ? t.clientX : t.clientY);
    };
    const onEnd = () => {
      this._endDrag(index);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend',  onEnd);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend',  onEnd);
  }

  // ── Drag — shared helpers ─────────────────────────────────────────────────

  private _beginDrag(index: number, pos: number) {
    this._draggingIndex = index;
    this._startPos      = pos;
    this._startSizeA    = this._sizes[index];
    this._startSizeB    = this._sizes[index + 1];

    const isH   = this.layout !== 'vertical';
    const gutter = this._gutters[index];
    const handle = this._handles[index];
    if (gutter) gutter.style.backgroundColor = 'var(--uwc-splitter-gutter-active-bg, rgba(99,102,241,0.12))';
    if (handle) {
      handle.style.background  = 'var(--uwc-splitter-handle-active, #6366f1)';
      handle.style.transform   = isH ? 'scaleX(2.25)' : 'scaleY(2.25)';
    }

    emit(this, 'uwc-resize-start', { detail: { sizes: [...this._sizes] } });
  }

  private _moveDrag(index: number, pos: number) {
    const isH           = this.layout !== 'vertical';
    const containerRect = this.getBoundingClientRect();
    const containerSize = isH ? containerRect.width : containerRect.height;
    const gutterTotal   = (this._panels.length - 1) * this.gutterSize;
    const available     = containerSize - gutterTotal;
    if (available <= 0) return;

    const deltaPct = ((pos - this._startPos) / available) * 100;
    const minA     = this._panels[index].minSize;
    const minB     = this._panels[index + 1].minSize;
    const total    = this._startSizeA + this._startSizeB;

    const newA = Math.max(minA, Math.min(total - minB, this._startSizeA + deltaPct));
    const newB = total - newA;

    this._sizes[index]     = newA;
    this._sizes[index + 1] = newB;
    this._applyPanelStyles();

    const gutter = this._gutters[index];
    if (gutter) gutter.setAttribute('aria-valuenow', String(Math.round(newA)));

    emit(this, 'uwc-resize', { detail: { sizes: [...this._sizes] } });
  }

  private _endDrag(index: number) {
    this._draggingIndex = -1;

    const gutter = this._gutters[index];
    const handle = this._handles[index];
    if (gutter) gutter.style.backgroundColor = 'var(--uwc-splitter-gutter-bg, transparent)';
    if (handle) {
      handle.style.background = 'var(--uwc-splitter-handle-color, rgba(0,0,0,0.18))';
      handle.style.transform  = '';
    }

    this._saveState();
    emit(this, 'uwc-resize-end', { detail: { sizes: [...this._sizes] } });
  }

  // ── Keyboard ──────────────────────────────────────────────────────────────

  private _onGutterKeydown(e: KeyboardEvent, index: number) {
    const isH  = this.layout !== 'vertical';
    const minA = this._panels[index].minSize;
    const minB = this._panels[index + 1].minSize;
    const total = this._sizes[index] + this._sizes[index + 1];
    let delta  = 0;

    if (isH) {
      if (e.key === 'ArrowLeft')  delta = -this.step;
      if (e.key === 'ArrowRight') delta = +this.step;
      if (e.key === 'Home')       delta = -(this._sizes[index] - minA);
      if (e.key === 'End')        delta = (total - minB) - this._sizes[index];
    } else {
      if (e.key === 'ArrowUp')    delta = -this.step;
      if (e.key === 'ArrowDown')  delta = +this.step;
      if (e.key === 'Home')       delta = -(this._sizes[index] - minA);
      if (e.key === 'End')        delta = (total - minB) - this._sizes[index];
    }
    if (!delta) return;
    e.preventDefault();

    const newA = Math.max(minA, Math.min(total - minB, this._sizes[index] + delta));
    this._sizes[index]     = newA;
    this._sizes[index + 1] = total - newA;
    this._applyPanelStyles();

    const gutter = this._gutters[index];
    if (gutter) gutter.setAttribute('aria-valuenow', String(Math.round(newA)));

    this._saveState();
    emit(this, 'uwc-resize', { detail: { sizes: [...this._sizes] } });
  }

  // ── State persistence ─────────────────────────────────────────────────────

  private _loadState(): number[] | null {
    if (!this.stateKey) return null;
    try {
      const storage = this.stateStorage === 'session' ? sessionStorage : localStorage;
      const raw     = storage.getItem(this.stateKey);
      if (raw) return JSON.parse(raw) as number[];
    } catch { /* ignore */ }
    return null;
  }

  private _saveState() {
    if (!this.stateKey) return;
    try {
      const storage = this.stateStorage === 'session' ? sessionStorage : localStorage;
      storage.setItem(this.stateKey, JSON.stringify(this._sizes));
    } catch { /* ignore */ }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Return a copy of the current panel sizes (percentages). */
  getSizes(): number[] {
    return [...this._sizes];
  }

  /**
   * Reset panels to their initial `size` attribute values (or equal split) and
   * clear any persisted state.
   */
  resetSizes() {
    if (this.stateKey) {
      const storage = this.stateStorage === 'session' ? sessionStorage : localStorage;
      storage.removeItem(this.stateKey);
    }
    this._cleanup();
    this._sizes = this._defaultSizes();
    this._applyPanelStyles();
    this._injectGutters();
    emit(this, 'uwc-resize-end', { detail: { sizes: [...this._sizes] } });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  protected override render() {
    return html`<slot @slotchange=${this._onSlotChange}></slot>`;
  }

  /**
   * Fired when slotted children change. Only re-initialize when the number of
   * actual panels changes (not when we inject/remove gutters, which are plain
   * divs filtered out by the instanceof check).
   */
  private _onSlotChange() {
    const newPanels = Array.from(this.children).filter(
      (el): el is UwcSplitterPanel => el instanceof UwcSplitterPanel,
    );
    if (newPanels.length !== this._panels.length) {
      this._cleanup();
      this._setup();
    }
  }
}

// ── Global type augmentation ──────────────────────────────────────────────────

declare global {
  interface HTMLElementTagNameMap {
    'uwc-splitter':       UwcSplitter;
    'uwc-splitter-panel': UwcSplitterPanel;
  }
}
